import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'theme/jss-types';
import { newProductStyles } from './new-product.styles';
import { Product } from './settings.types';
import {
    Form,
    Input,
    message,
    Modal,
    Select,
    Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { productFields } from './new-product.constants';
import { saveProduct, updateProduct } from 'network/settings-product';
import {
    getDangerousGoodData,
} from 'network/consignments.api';
import { useTranslation } from 'react-i18next';

interface NewProductProps extends StylesProps<ReturnType<typeof newProductStyles>> {
    product?: Product;
    declaredCurrencyList: any[];
    onClose: (reload?: boolean) => void;
}
const NewProduct = (props: NewProductProps) => {
    const {
        classes,
        onClose,
        product,
        declaredCurrencyList,
    } = props;
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = React.useState<boolean>(false);
    const [
        dangerousGoodUnNumber,
        setDangerousGoodUnNumber,
    ] = React.useState<string | undefined>(product?.dangerousGoodUnNumber);

    const prefillForm = () => {
        form.setFieldsValue({
            [productFields.code.key]: product?.code,
            [productFields.name.key]: product?.name,
            [productFields.description.key]: product?.description,
            [productFields.declaredValue.key]: product?.declaredValue,
            [productFields.height.key]: product?.height,
            [productFields.width.key]: product?.width,
            [productFields.length.key]: product?.length,
            [productFields.weight.key]: product?.weight,
            [productFields.declaredCurrency.key]: product?.declaredCurrency,
            [productFields.dangerousGoodUnNumber.key]: product?.dangerousGoodUnNumber,
            [productFields.dangerousGoodName.key]: product?.dangerousGoodName,
        });
    };

    React.useEffect(() => {
        prefillForm();
    }, []);

    const renderLabel = (key: string) => {
        const required = productFields[key].required;
        const info = productFields[key].info;

        return (
            <div
                className={classes.label}
            >
                <span>{t(productFields[key].pretty_name)}</span>
                {required && <span>*</span>}
                {info && (
                    <Tooltip title={t(info)}>
                        <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                    </Tooltip>
                )}
            </div>
        );
    };

    const validateInput = (key: string, value: any, callback: any) => {
        if (!value) {
            return callback(`You've missed this field. ${productFields[key].errormsg}`);
        }
        return callback();
    };

    const validateNumber = (key: string, value: any, callback: any) => {
        if (!value) {
            return callback(`You've missed this field. ${productFields[key].errormsg}`);
        }
        if (value < 0) {
            return callback(`${key} can not be negative`);
        }
        return callback();
    };

    const validateUnNumberRegex = (value: string) => {
        const regex = /^[Uu][Nn](?:[0-2]\d{3}|3[0-5]\d{2}|3600)$/;
        return regex.test(value);
    };

    const validateUnNumber = (value: any, callback: any) => {
        if (!value) {
            return callback();
        }
        if (!validateUnNumberRegex(value)) {
            return callback('UN Number can be from UN000 to UN3600');
        }
        return callback();
    };

    const renderInput = (key: string) => {
        return (
            <div className={classes.formParent}>
                {renderLabel(key)}
                <Form.Item
                    name={key}
                    className={classes.formItem}
                    rules={[{
                        validator: (rule, value, cb) => validateInput(key, value, cb),
                    }]}
                >
                    <Input
                        disabled={Boolean(product?.code && key === productFields.code.key)}
                        name={key}
                        max={50}
                        placeholder={t(productFields[key].placeholder)}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderInputNumber = (key: string) => {
        return (
            <div className={classes.formParent}>
                {renderLabel(key)}
                <Form.Item
                    name={key}
                    className={classes.formItem}
                    valuePropName="value"
                    rules={[{
                        validator: (rule, value, cb) => validateNumber(key, value, cb),
                    }]}
                >
                    <Input
                        type="number"
                        name={key}
                        min={0}
                        max={50}
                        placeholder={t(productFields[key].placeholder)}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderDescription = (key: string) => {
        return (
            <div
                className={classes.formParent}
                style={{ width: '94%' }}
            >
                {renderLabel(key)}
                <Form.Item
                    name={key}
                    className={classes.formItem}
                    rules={[{
                        validator: (rule, value, cb) => validateInput(key, value, cb),
                    }]}
                >
                    <Input.TextArea
                        name={key}
                        autoSize={{
                            minRows: 1,
                            maxRows: 4,
                        }}
                        placeholder={t(productFields[key].placeholder)}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderDimensions = () => {
        return (
            <div className={classes.flexRow}>
                {renderInputNumber(productFields.length.key)}
                {renderInputNumber(productFields.width.key)}
                {renderInputNumber(productFields.height.key)}
            </div>
        );
    };

    const fetchDangerousGoodsData = async (unNumberValue: string) => {
        if (unNumberValue && validateUnNumberRegex(unNumberValue)) {
            const unNumberSanitized = unNumberValue?.toString()?.toUpperCase();
            const response = await getDangerousGoodData({ unNumber: unNumberSanitized });
            if (response.isSuccess) {
                form.setFieldsValue({
                    [productFields.dangerousGoodName.key]: response.data?.shipping_name,
                });
            } else {
                message.error(response?.errorMessage || 'Invalid input!');
            }
        } else {
            form.setFieldsValue({
                [productFields.dangerousGoodName.key]: '',
            });
        }
    };

    const renderUnNumberField = () => {
        return (
            <div style={{ flex: 1 }}>
                {renderLabel(productFields.dangerousGoodUnNumber.key)}
                <Form.Item
                    name={productFields.dangerousGoodUnNumber.key}
                    className={classes.formItem}
                    valuePropName="value"
                    rules={[{
                        validator: (rule, value, cb) => validateUnNumber(value, cb),
                    }]}
                >
                    <Input
                        name={productFields.dangerousGoodUnNumber.key}
                        max={50}
                        placeholder={t(productFields[productFields.dangerousGoodUnNumber.key].placeholder)}
                        onChange={(e) => {
                            setDangerousGoodUnNumber(e.target.value);
                            fetchDangerousGoodsData(e.target.value);
                        }}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderDangerousGoodsField = () => {
        return (
            <div style={{ flex: 1 }}>
                {renderLabel(productFields.dangerousGoodName.key)}
                <Form.Item
                    name={productFields.dangerousGoodName.key}
                    className={classes.formItem}
                >
                    <Input.TextArea
                        disabled
                        placeholder={t(productFields[productFields.dangerousGoodName.key].placeholder)}
                    />
                </Form.Item>
            </div>
        );
    };

    const renderForm = () => {
        return (
            <Form
                form={form}
                className={classes.form}
                onValuesChange={(changedValues) => {
                    if (changedValues[productFields.dangerousGoodUnNumber.key] !== undefined) {
                        setDangerousGoodUnNumber(changedValues[productFields.dangerousGoodUnNumber.key]);
                    }
                }}
            >
                {renderInput(productFields.code.key)}
                {renderInput(productFields.name.key)}
                {renderDescription(productFields.description.key)}
                {renderDimensions()}
                {renderInputNumber(productFields.weight.key)}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                        {renderLabel(productFields.declaredValue.key)}
                        <Form.Item
                            name={productFields.declaredValue.key}
                            className={classes.formItem}
                            valuePropName="value"
                            rules={[{
                                validator: (rule, value, cb) => validateNumber(productFields.declaredValue.key, value, cb),
                            }]}
                        >
                            <Input
                                type="number"
                                name={productFields.declaredValue.key}
                                min={0}
                                max={50}
                                placeholder={t(productFields[productFields.declaredValue.key].placeholder)}
                            />
                        </Form.Item>
                    </div>
                    {(declaredCurrencyList || []).length
                        ? (
                            <div style={{ flex: 1 }}>
                                {renderLabel(productFields.declaredCurrency.key)}
                                <Form.Item
                                    name={productFields.declaredCurrency.key}
                                    className={classes.formItem}
                                    initialValue={declaredCurrencyList[0]}
                                    rules={[{
                                        validator: (rule, value, cb) => validateInput(
                                            productFields.declaredCurrency.key,
                                            value,
                                            cb,
                                        ),
                                    }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder={t('declared_currecny')}
                                        options={declaredCurrencyList?.map((currency) => ({
                                            value: currency,
                                            label: currency,
                                        }))}
                                        allowClear
                                    />
                                </Form.Item>
                            </div>
                        ) : null}
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {renderUnNumberField()}
                    {
                        dangerousGoodUnNumber
                        && dangerousGoodUnNumber.trim() !== ''
                        && validateUnNumberRegex(dangerousGoodUnNumber)
                        && renderDangerousGoodsField()
                    }
                </div>
            </Form>
        );
    };

    const handleSave = async () => {
        const formValues = await form.validateFields();
        if (formValues.errorFields?.length) {
            return;
        }
        setSaving(true);
        let response;
        const apiBody = form.getFieldsValue();
        if (product?.id) {
            const updatePayload = {
                ...product,
                ...apiBody,
            };

            const dangerousGoodUnNum = apiBody[productFields.dangerousGoodUnNumber.key];
            if (!dangerousGoodUnNum || dangerousGoodUnNum.trim() === '') {
                updatePayload[productFields.dangerousGoodUnNumber.key] = null;
                updatePayload[productFields.dangerousGoodName.key] = null;
            }

            response = await updateProduct(updatePayload);
        } else {
            response = await saveProduct(apiBody);
        }
        setSaving(false);
        if (response?.isSuccess) {
            message.success(product?.id ? t('update_product_success') : t('add_product_success'));
            onClose(true);
        } else {
            message.error(response.errorMessage);
        }
    };

    return (
        <Modal
            className={classes.modal}
            closable
            visible
            title={product?.id ? t('edit_product').toUpperCase() : t('add_product').toUpperCase()}
            width="50%"
            onCancel={() => onClose(false)}
            onOk={() => handleSave()}
            okButtonProps={{
                loading: saving,
            }}
            okText={t('save').toUpperCase()}
        >
            {renderForm()}
        </Modal>
    );
};

export default withStyles(newProductStyles)(NewProduct);
